/**
 * Prognoza produkcji i autokonsumpcji miesiąc po miesiącu.
 *
 * PO CO MIESIĄCE, SKORO MAMY ROCZNĄ SUMĘ: cała rzecz w fotowoltaice polega na tym,
 * że produkcja i zużycie rozjeżdżają się w czasie. W czerwcu instalacja produkuje
 * kilkakrotnie więcej, niż dom zużywa, i nadwyżki idą do sieci za jedną trzecią ceny;
 * w grudniu autokonsumpcja jest bliska stu procent, bo wszystko zużywa się na miejscu.
 * Roczna średnia zaciera obie rzeczy naraz — a to na nich stoi cała opłacalność.
 *
 * Model autokonsumpcji NIE JEST tu pisany od nowa: bierzemy krzywą nasycenia
 * z `mocKalkulator.js` i podstawiamy do niej miesięczny stosunek produkcji do zużycia.
 * Dwa niezgodne ze sobą modele tego samego zjawiska byłyby gorsze niż brak drugiego.
 */

import { MIESIACE, UZYSK_MIESIECZNY } from './pvgisPolska'
import { UZYSK_MIESIECZNY_RO } from './pvgisRumunia'
import { regionOkregu } from './regionyRumunii'
import { jestPolska } from '../kraj'
import {
  PROFILE, ZACIENIENIA, normalizujDodatki, samowystarczalnoscZOkres,
} from './mocKalkulator'

const DNI_MIESIACA = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

/* ═══════════════════════════════════════════════════════════════════════════
   KRZYWA AUTOKONSUMPCJI W ROZDZIELCZOŚCI MIESIĘCZNEJ

   Kalkulator mocy używa krzywej wykładniczej `C·(1−e^(−k·r))`, kalibrowanej w jednym
   punkcie: przy produkcji równej rocznemu zużyciu (r = 1). Do wymiarowania instalacji
   to wystarcza, bo tam r kręci się wokół jedynki.

   W ujęciu miesięcznym r skacze od ok. 0,3 w grudniu do ponad 3 w czerwcu — i przy
   niskim r ta krzywa się załamuje. Jej nachylenie w zerze wynosi C·k ≈ 0,37, co znaczy
   „nawet przy śladowej produkcji dwie trzecie oddasz do sieci". To nieprawda: gdy
   instalacja produkuje znacznie mniej, niż dom w tym czasie zużywa, prawie wszystko
   idzie na własne potrzeby. Zmierzone na wyniku: model dawał w grudniu 32%
   autokonsumpcji zamiast realnych ok. 45–55%.

   Dlatego tutaj używamy rodziny z TRZEMA warunkami, każdy o fizycznym znaczeniu:
     1. sufit  S(∞) = 1 − pozaSloncem   — bez magazynu nie pokryjesz zużycia nocnego,
     2. punkt  S(1) = autokonsumpcja    — zgodność z kalkulatorem mocy co do joty,
     3. nachylenie w zerze S'(0) = φ₀   — udział produkcji zużytej przy znikomej produkcji.

   S(r) = C · (1 − (1 + r/b)^(−a)),  a = b·φ₀/C

   Parametry `a` i `b` nie są wpisane z palca — wyliczamy je z tych trzech warunków.
   Kalkulatora mocy NIE zmieniamy: obie krzywe spotykają się dokładnie w r = 1, więc
   dobór mocy i prognoza mówią to samo o tym samym, a różnią się tylko tam, gdzie
   tamta nigdy nie była sprawdzana.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Udział produkcji zużytej na miejscu, gdy produkcja jest znikoma wobec zużycia.
 * Nie 1,0: nawet w grudniu produkcja kumuluje się w kilku godzinach koło południa,
 * a dom w tym czasie bierze głównie obciążenie podstawowe, więc część szczytu
 * i tak ucieka do sieci.
 */
const UDZIAL_PRZY_ZNIKOMEJ_PRODUKCJI = 0.8

const dopasowaneParametry = new Map()

const parametryKrzywej = (p) => {
  if (dopasowaneParametry.has(p.key)) return dopasowaneParametry.get(p.key)

  const sufit = 1 - p.pozaSloncem
  const cel = p.autokonsumpcja / sufit          // S(1)/C
  const nachylenie = UDZIAL_PRZY_ZNIKOMEJ_PRODUKCJI / sufit   // a/b

  // f(b) rośnie monotonicznie — bisekcja jest tu wystarczająca i deterministyczna.
  const f = (b) => 1 - (1 + 1 / b) ** (-nachylenie * b)
  let lo = 1e-4
  let hi = 500
  for (let i = 0; i < 80; i += 1) {
    const sr = (lo + hi) / 2
    if (f(sr) < cel) lo = sr
    else hi = sr
  }
  const b = (lo + hi) / 2
  const wynik = { sufit, a: nachylenie * b, b }
  dopasowaneParametry.set(p.key, wynik)
  return wynik
}

/** Samowystarczalność bez magazynu — wersja poprawna także dla skrajnych miesięcy. */
export const udzialMiesieczny = (p, r) => {
  const { sufit, a, b } = parametryKrzywej(p)
  // Nie więcej niż produkcja: przy r bliskim zeru sama krzywa mogłaby obiecać
  // pokrycie większe niż to, co w ogóle wyprodukowano.
  return Math.min(r, sufit * (1 - (1 + r / b) ** -a))
}

/** Utrata mocy modułów. Typowa gwarancja producenta to ok. 0,5%/rok po pierwszym roku. */
export const DEGRADACJA_ROCZNA = 0.005

/**
 * Sezonowość zużycia domu jednorodzinnego BEZ ogrzewania elektrycznego.
 * Zimą krócej jest jasno i pracuje pompa obiegowa; latem zostaje sama bytówka.
 * Amplituda celowo umiarkowana — dom z pompą ciepła to zupełnie inny przebieg
 * i ma własny wiersz niżej.
 */
const SEZON_BEZ_OGRZEWANIA = [1.15, 1.12, 1.05, 0.95, 0.88, 0.83, 0.82, 0.83, 0.90, 1.02, 1.10, 1.15]

/**
 * Sezonowość samej pompy ciepła. Nie „trochę więcej zimą" — pompa w styczniu bierze
 * kilkanaście razy więcej niż w lipcu, gdzie zostaje jej tylko podgrzewanie wody.
 */
const SEZON_POMPY = [2.30, 2.10, 1.45, 0.75, 0.35, 0.20, 0.18, 0.18, 0.30, 0.85, 1.65, 2.20]

/** Skalowanie do sumy 12 — dzięki temu wektor mnoży średnią miesięczną, cokolwiek w nim stoi. */
const doSredniej = (wektor) => {
  const suma = wektor.reduce((a, b) => a + b, 0)
  return wektor.map((v) => (v * 12) / suma)
}

/**
 * Rozkład rocznego zużycia na miesiące.
 *
 * Pompa ciepła nie przestawia przebiegu na sztywno, tylko MIESZA SIĘ z bytówką
 * proporcjonalnie do swojego udziału w zużyciu. Dom, w którym pompa to 4500 z 9000 kWh,
 * ma inny przebieg niż taki, gdzie to 4500 z 20 000 — sztywne przełączenie profilu
 * gubiłoby tę różnicę.
 */
export const zuzycieMiesieczne = (zuzycieRoczne, dodatki = []) => {
  const rozbite = normalizujDodatki(dodatki)
  const pompaKwh = rozbite.filter((d) => d.key === 'pompa').reduce((s, d) => s + d.kwh, 0)
  const udzialPompy = zuzycieRoczne > 0 ? Math.min(1, pompaKwh / zuzycieRoczne) : 0

  const bytowa = doSredniej(SEZON_BEZ_OGRZEWANIA)
  const pompa = doSredniej(SEZON_POMPY)
  const mieszany = bytowa.map((v, i) => v * (1 - udzialPompy) + pompa[i] * udzialPompy)

  return mieszany.map((wsp) => (zuzycieRoczne / 12) * wsp)
}

/** Uzysk jednostkowy [kWh/kWp] miesiąc po miesiącu, po uwzględnieniu zacienienia. */
export const uzyskMiesieczny = (wojewodztwo, ustawienie, zacienienie = 'brak') => {
  // W wersji rumuńskiej pole `wojewodztwo` niesie okręg (`ro_cluj`…), a tabela jest
  // per region rozwoju; patrz regionyRumunii.js.
  const woj = jestPolska
    ? (UZYSK_MIESIECZNY[wojewodztwo] || UZYSK_MIESIECZNY.mazowieckie)
    : UZYSK_MIESIECZNY_RO[regionOkregu(wojewodztwo)]
  const tabela = woj[ustawienie] || woj.poludnie
  const z = ZACIENIENIA.find((x) => x.key === zacienienie) || ZACIENIENIA[0]
  return tabela.map((v) => v * z.wsp)
}

/**
 * Produkcja pierwszego roku podana z zewnątrz: dokładnie 12 liczb skończonych i nieujemnych.
 * Zła tablica to błąd wołającego, więc go zgłaszamy, zamiast po cichu wrócić do tabeli
 * PVGIS: wynik dla innej instalacji niż ta na ekranie byłby cichą nieprawdą.
 */
const produkcjaPodana = (wartosci) => {
  if (wartosci == null) return null
  const tablica = Array.isArray(wartosci) || ArrayBuffer.isView(wartosci) ? Array.from(wartosci) : null
  if (!tablica || tablica.length !== 12 || !tablica.every((v) => Number.isFinite(v) && v >= 0)) {
    throw new Error('prognoza: produkcjaMiesiecznaKwh musi mieć 12 liczb nieujemnych')
  }
  return tablica
}

/** Sprawność cyklu magazynu z karty (0–1]; `null` zostawia wartość z kalkulatora mocy. */
const sprawnoscPodana = (wartosc) => {
  if (wartosc == null) return undefined
  if (!Number.isFinite(wartosc) || wartosc <= 0 || wartosc > 1) {
    throw new Error('prognoza: sprawnoscMagazynu musi być ułamkiem z przedziału (0, 1]')
  }
  return wartosc
}

/**
 * Pełna prognoza dla jednego roku eksploatacji.
 *
 * `rok` liczony od 1. Wpływa wyłącznie przez degradację modułów — pozostałe wielkości
 * (zużycie, ceny) zmienia dopiero moduł opłacalności, żeby ta funkcja pozostała
 * czysto energetyczna.
 *
 * `produkcjaMiesiecznaKwh` (opcjonalnie): produkcja PIERWSZEGO roku w 12 miesiącach,
 * policzona poza tym plikiem, np. w projekcie dachu 3D dla prawdziwych połaci i cienia
 * (`lib/projekt3d/analiza.js`). Zastępuje wtedy `kwp × uzysk` z tabeli PVGIS, więc
 * `wojewodztwo`, `ustawienie` i `zacienienie` nie zmieniają produkcji; degradacja z `rok`
 * działa jak zwykle. `uzyskJednostkowy` to wtedy roczna suma przez `kwp` (bez mocy: null).
 *
 * `sprawnoscMagazynu` (opcjonalnie, 0–1): sprawność cyklu magazynu z jego karty.
 * Bez niej model magazynu liczy z SPRAWNOSC_MAGAZYNU kalkulatora mocy (0,9).
 */
export const prognoza = ({
  kwp,
  wojewodztwo = 'mazowieckie',
  ustawienie = 'poludnie',
  zacienienie = 'brak',
  zuzycieRoczne = 0,
  profil = 'klasyczny',
  dodatki = [],
  pojemnoscMagazynuKwh = 0,
  rok = 1,
  produkcjaMiesiecznaKwh = null,
  sprawnoscMagazynu = null,
}) => {
  const p = PROFILE.find((x) => x.key === profil) || PROFILE[1]
  const podana = produkcjaPodana(produkcjaMiesiecznaKwh)
  const sprawnoscCyklu = sprawnoscPodana(sprawnoscMagazynu)
  // Tabela PVGIS tylko wtedy, gdy produkcji nie podano. Iloczyn `kwp * u` liczony jak
  // dotąd, czyli przed degradacją, więc wynik bez nowych pól jest identyczny co do bitu.
  const uzysk = podana ? null : uzyskMiesieczny(wojewodztwo, ustawienie, zacienienie)
  const pierwszyRok = podana || uzysk.map((u) => kwp * u)
  const zuzycie = zuzycieMiesieczne(zuzycieRoczne, dodatki)
  const sprawnosc = (1 - DEGRADACJA_ROCZNA) ** Math.max(0, rok - 1)

  const miesiace = pierwszyRok.map((wPierwszymRoku, i) => {
    const produkcja = wPierwszymRoku * sprawnosc
    const zuz = zuzycie[i]

    // Bez znanego zużycia nie ma czego dzielić na autokonsumpcję i nadwyżkę —
    // zwracamy samą produkcję zamiast zmyślać podział.
    if (zuz <= 0) {
      return {
        nazwa: MIESIACE[i], dni: DNI_MIESIACA[i],
        produkcjaKwh: produkcja, zuzycieKwh: 0,
        autokonsumpcjaKwh: null, nadwyzkaKwh: null, zSieciKwh: null,
      }
    }

    const r = produkcja / zuz
    const bazowa = udzialMiesieczny(p, r)
    const udzial = pojemnoscMagazynuKwh > 0
      ? samowystarczalnoscZOkres(p, r, pojemnoscMagazynuKwh, zuz, DNI_MIESIACA[i], bazowa, sprawnoscCyklu)
      : bazowa

    const pokryte = zuz * udzial
    return {
      nazwa: MIESIACE[i], dni: DNI_MIESIACA[i],
      produkcjaKwh: produkcja,
      zuzycieKwh: zuz,
      autokonsumpcjaKwh: pokryte,
      nadwyzkaKwh: Math.max(0, produkcja - pokryte),
      zSieciKwh: Math.max(0, zuz - pokryte),
    }
  })

  const suma = (pole) => miesiace.reduce((s, m) => s + (m[pole] || 0), 0)
  const produkcjaRoczna = suma('produkcjaKwh')
  const autokonsumpcja = suma('autokonsumpcjaKwh')

  return {
    miesiace,
    rocznie: {
      produkcjaKwh: produkcjaRoczna,
      zuzycieKwh: suma('zuzycieKwh'),
      autokonsumpcjaKwh: zuzycieRoczne > 0 ? autokonsumpcja : null,
      nadwyzkaKwh: zuzycieRoczne > 0 ? suma('nadwyzkaKwh') : null,
      zSieciKwh: zuzycieRoczne > 0 ? suma('zSieciKwh') : null,
      autokonsumpcjaProc: produkcjaRoczna > 0 && zuzycieRoczne > 0
        ? Math.round((autokonsumpcja / produkcjaRoczna) * 100) : null,
      samowystarczalnoscProc: zuzycieRoczne > 0
        ? Math.round((autokonsumpcja / zuzycieRoczne) * 100) : null,
    },
    uzyskJednostkowy: uzysk
      ? Math.round(uzysk.reduce((a, b) => a + b, 0))
      : (kwp > 0 ? Math.round(podana.reduce((a, b) => a + b, 0) / kwp) : null),
  }
}
